import {
  BODY_INPUT_DECIMAL_PLACE,
  BODY_SMALLEST_UNIT_DECIMAL_PLACE,
} from "config"
import { IBodyDisplayUnits } from "features/profile/profileTypes"
import { formatDisplayNumber, roundByDecimalPlace } from "utils"

const POUND_TO_GRAM_RATE = 453.592
const KG_TO_GRAM_RATE = 1000

const DEFAULT_IMPERIAL_BODY_WEIGHT = 160
const DEFAULT_METRIC_BODY_WEIGHT = 72

export const getDefaultBodyWeight = (bodyDisplayUnits: IBodyDisplayUnits) => {
  return bodyDisplayUnits === "IMPERIAL"
    ? DEFAULT_IMPERIAL_BODY_WEIGHT
    : DEFAULT_METRIC_BODY_WEIGHT
}

export const gramToPound = (gram: number) => {
  return roundByDecimalPlace(
    gram / POUND_TO_GRAM_RATE,
    BODY_INPUT_DECIMAL_PLACE,
  )
}

export const poundToGram = (pound: number) => {
  return roundByDecimalPlace(
    pound * POUND_TO_GRAM_RATE,
    BODY_SMALLEST_UNIT_DECIMAL_PLACE,
  )
}

export const gramToKg = (gram: number) => {
  return roundByDecimalPlace(
    gram / KG_TO_GRAM_RATE,
    BODY_INPUT_DECIMAL_PLACE,
  )
}

export const kgToGram = (kg: number) => {
  return roundByDecimalPlace(
    kg * KG_TO_GRAM_RATE,
    BODY_SMALLEST_UNIT_DECIMAL_PLACE,
  )
}

export const getDisplayWeight = (
  bodyDisplayUnits: IBodyDisplayUnits,
  weight?: number,
) => {
  if (!weight) {
    return "..."
  }

  return bodyDisplayUnits === "IMPERIAL"
    ? gramToPound(weight)
    : gramToKg(weight)
}

export const getDisplayWeightWithUnit = (
  bodyDisplayUnits: IBodyDisplayUnits,
  weight?: number,
) => {
  if (!weight) {
    return "..."
  }

  const displayWeight = getDisplayWeight(bodyDisplayUnits, weight) as number
  const formattedWeight = formatDisplayNumber(
    displayWeight,
    BODY_INPUT_DECIMAL_PLACE,
  )

  if (bodyDisplayUnits === "IMPERIAL") {
    // alway display lb for imperial
    return `${formattedWeight} lb`
  }

  return `${formattedWeight} kg`
}

export const getGramWeight = (
  bodyDisplayUnits: IBodyDisplayUnits,
  weight: number,
) => {
  return bodyDisplayUnits === "IMPERIAL"
    ? poundToGram(weight)
    : kgToGram(weight)
}

export const kgToPound = (kg: number) => {
  return gramToPound(kgToGram(kg))
}

export const poundToKg = (pound: number) => {
  return gramToKg(poundToGram(pound))
}

export const MIN_KG_BODY_WEIGHT = 20
export const MAX_KG_BODY_WEIGHT = 200

export const MIN_LB_BODY_WEIGHT = 45
export const MAX_LB_BODY_WEIGHT = 450

export const getMaxBodyWeight = (bodyDisplayUnits: IBodyDisplayUnits) => {
  return bodyDisplayUnits === "IMPERIAL"
    ? MAX_LB_BODY_WEIGHT
    : MAX_KG_BODY_WEIGHT
}

export const getMinBodyWeight = (bodyDisplayUnits: IBodyDisplayUnits) => {
  return bodyDisplayUnits === "IMPERIAL"
    ? MIN_LB_BODY_WEIGHT
    : MIN_KG_BODY_WEIGHT
}

export const getBodyWeightValueFromGram = (
  bodyDisplayUnits: IBodyDisplayUnits,
  gram: number,
) => {
  return bodyDisplayUnits === "IMPERIAL" ? gramToPound(gram) : gramToKg(gram)
}

const TARGET_WEIGHT_MAX_DIFF_IMPERIAL = 20
const TARGET_WEIGHT_MAX_DIFF_METRIC = 12

export const getTargetWeightRange = (
  bodyDisplayUnits: IBodyDisplayUnits,
  currentWeightInGrams: number,
  typeName: "WEIGHT_LOSS" | "BUILDING",
): { min: number; max: number } => {
  const currentWeightInDisplayUnit =
    bodyDisplayUnits === "IMPERIAL"
      ? gramToPound(currentWeightInGrams)
      : gramToKg(currentWeightInGrams)

  const maxAllowedDifference =
    bodyDisplayUnits === "IMPERIAL"
      ? TARGET_WEIGHT_MAX_DIFF_IMPERIAL
      : TARGET_WEIGHT_MAX_DIFF_METRIC

  const minBodyWeight = getMinBodyWeight(bodyDisplayUnits)
  const maxBodyWeight = getMaxBodyWeight(bodyDisplayUnits)

  if (typeName === "WEIGHT_LOSS") {
    const lowerBound = roundByDecimalPlace(
      currentWeightInDisplayUnit - maxAllowedDifference,
      1,
    )
    return {
      min: Math.max(minBodyWeight, lowerBound),
      max: roundByDecimalPlace(currentWeightInDisplayUnit - 0.1, 1),
    }
  }

  const upperBound = roundByDecimalPlace(
    currentWeightInDisplayUnit + maxAllowedDifference,
    1,
  )
  return {
    min: roundByDecimalPlace(currentWeightInDisplayUnit + 0.1, 1),
    max: Math.min(maxBodyWeight, upperBound),
  }
}
